import { CheckCircleIcon, ExclamationTriangleIcon, XCircleIcon, CloudArrowUpIcon } from '@heroicons/react/24/outline';
import { useBackups } from '../hooks/useBackups';

// Friendlier names than the raw database identifiers.
const DB_LABELS = {
  dashboard_prod: 'Dashboard',
  fsm_prod: 'Field Service',
  inspections_prod: 'Health & Safety',
  logistics_prod: 'Logistics',
  tech_prod: 'IT & Infosec',
  training_prod: 'Training',
};

const VERDICTS = {
  ok: { label: 'Backed up', icon: CheckCircleIcon, chip: 'bg-emerald-100 text-emerald-800', text: 'text-emerald-700' },
  stale: { label: 'Out of date', icon: ExclamationTriangleIcon, chip: 'bg-amber-100 text-amber-900', text: 'text-amber-700' },
  missing: { label: 'No backup', icon: XCircleIcon, chip: 'bg-rose-100 text-rose-800', text: 'text-rose-700' },
};

const OVERALL = {
  ok: { card: 'border-emerald-300 bg-emerald-50/40 text-emerald-800', msg: 'All databases backed up in the last 24 hours, on this server and offsite.' },
  stale: { card: 'border-amber-300 bg-amber-50/50 text-amber-900', msg: 'One or more backups are out of date — a nightly run may have been missed.' },
  missing: { card: 'border-rose-300 bg-rose-50/50 text-rose-800', msg: 'One or more databases have no backup. Investigate the nightly job now.' },
};

function fmtSize(bytes) {
  if (bytes == null) return '—';
  if (bytes < 1048576) return `${Math.max(1, Math.round(bytes / 1024))} KB`;
  return `${(bytes / 1048576).toFixed(1)} MB`;
}
function ago(value) {
  if (!value) return 'never';
  const mins = Math.round((Date.now() - new Date(value).getTime()) / 60000);
  if (mins < 60) return `${mins}m ago`;
  const h = Math.round(mins / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.round(h / 24)}d ago`;
}
function when(value) {
  if (!value) return '—';
  return new Date(value).toLocaleString('en-GB', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
}

function Backups() {
  const { data, isLoading, isError, error } = useBackups();

  if (isError) {
    const status = error?.response?.status;
    return (
      <div className="mx-auto max-w-2xl p-6">
        <div className="rounded-lg border border-amber-300 bg-amber-50 p-4 text-sm text-amber-800">
          {status === 403 ? 'This page is for full administrators only.' : `Couldn’t load backup status: ${error?.response?.data?.error || error.message}`}
        </div>
      </div>
    );
  }

  const overall = data?.overall && OVERALL[data.overall];

  return (
    <div className="mx-auto max-w-4xl p-6">
      <h1 className="mb-1 text-2xl font-bold text-gray-900">Backups</h1>
      <p className="mb-6 text-sm text-gray-500">
        Every database and all inspection photos are backed up automatically at
        {' '}{data?.scheduledAt || '02:00 daily'} — kept {data?.retentionDays || 14} days on this
        server and copied offsite to the storage box.
      </p>

      {isLoading && !data && <p className="text-sm text-gray-400">Loading…</p>}

      {overall && (
        <div className={`mb-6 rounded-lg border p-4 text-sm font-medium ${overall.card}`}>{overall.msg}</div>
      )}

      {data?.databases && (
        <div className="overflow-hidden rounded-lg border border-gray-200">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                {['Data', 'Status', 'Last backup', 'Size', 'Offsite'].map((h) => (
                  <th key={h} className="px-4 py-2.5 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 bg-white [font-variant-numeric:tabular-nums]">
              {data.databases.map((d) => {
                const v = VERDICTS[d.verdict] || VERDICTS.missing;
                const Icon = v.icon;
                return (
                  <tr key={d.name} className="hover:bg-brand-light/30">
                    <td className="px-4 py-2.5 text-sm font-medium text-gray-800">
                      {DB_LABELS[d.name] || d.name}
                      <span className="ml-1 text-xs text-gray-400">{d.name}</span>
                    </td>
                    <td className="px-4 py-2.5">
                      <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ${v.chip}`}>
                        <Icon className="h-3.5 w-3.5" /> {v.label}
                      </span>
                    </td>
                    <td className="px-4 py-2.5 text-sm text-gray-700">
                      {when(d.lastBackupAt)} <span className="text-gray-400">({ago(d.lastBackupAt)})</span>
                    </td>
                    <td className="px-4 py-2.5 text-sm text-gray-700">{fmtSize(d.sizeBytes)}</td>
                    <td className="px-4 py-2.5 text-sm">
                      {d.offsite
                        ? <span className="inline-flex items-center gap-1 text-emerald-700"><CheckCircleIcon className="h-4 w-4" /> Yes</span>
                        : <span className="text-rose-700">No</span>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {data?.uploads !== undefined && (
        <div className="mt-4 flex items-center gap-2 rounded-lg border border-gray-200 bg-white p-4 text-sm">
          <CloudArrowUpIcon className="h-5 w-5 text-brand" />
          <span className="font-medium text-gray-800">Uploaded files &amp; certs</span>
          {data.uploads
            ? <span className="text-gray-500">mirrored offsite · last updated {when(data.uploads.lastSyncAt)} ({ago(data.uploads.lastSyncAt)})</span>
            : <span className="text-rose-700">no offsite copy found</span>}
        </div>
      )}

      {data?.generatedAt && (
        <p className="mt-4 text-xs text-gray-400">Checked {ago(data.generatedAt)}</p>
      )}
    </div>
  );
}

export default Backups;
